import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { POSITION_NAMES, SHIFT_TYPES } from "@/lib/constants";
import { toast } from "sonner";

export default function FacilityNewRequest() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    position: "Personal Support Worker", count: 1, shift_date: "",
    start_time: "07:00", end_time: "15:00", shift_type: "Day",
    urgency: "standard", department: "", contact_person: "", contact_number: "",
    notes: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [sh, sm] = form.start_time.split(":").map(Number);
      const [eh, em] = form.end_time.split(":").map(Number);
      const totalHours = Math.max(1, (eh + em / 60) - (sh + sm / 60));
      await api.post("/requests", {
        ...form, count: Number(form.count), total_hours: totalHours,
        status: "submitted", facility_id: "auto",
      });
      toast.success("Staffing request submitted.");
      navigate("/facility/requests");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="facility-new-request">
      <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>New Staffing Request</h1>
      <Card>
        <CardHeader><CardTitle>Request details</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>Position required</Label>
                <Select value={form.position} onValueChange={(v) => upd("position", v)}>
                  <SelectTrigger data-testid="new-req-position"><SelectValue /></SelectTrigger>
                  <SelectContent>{POSITION_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Number required</Label>
                <Input type="number" min={1} value={form.count} onChange={(e) => upd("count", e.target.value)} data-testid="new-req-count" />
              </div>
            </div>

            <div className="grid sm:grid-cols-4 gap-3">
              <div><Label>Shift date</Label><Input type="date" value={form.shift_date} onChange={(e) => upd("shift_date", e.target.value)} required data-testid="new-req-shift-date" /></div>
              <div><Label>Start time</Label><Input type="time" value={form.start_time} onChange={(e) => upd("start_time", e.target.value)} required data-testid="new-req-start-time" /></div>
              <div><Label>End time</Label><Input type="time" value={form.end_time} onChange={(e) => upd("end_time", e.target.value)} required data-testid="new-req-end-time" /></div>
              <div>
                <Label>Shift type</Label>
                <Select value={form.shift_type} onValueChange={(v) => upd("shift_type", v)}>
                  <SelectTrigger data-testid="new-req-shift-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{SHIFT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => upd("urgency", v)}>
                  <SelectTrigger data-testid="new-req-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => upd("department", e.target.value)} placeholder="ICU, ER, Geriatrics…" data-testid="new-req-department" /></div>
              <div><Label>Contact phone</Label><Input value={form.contact_number} onChange={(e) => upd("contact_number", e.target.value)} required data-testid="new-req-contact-phone" /></div>
            </div>

            <div><Label>Contact person on-site</Label><Input value={form.contact_person} onChange={(e) => upd("contact_person", e.target.value)} required data-testid="new-req-contact-person" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Skills required, special notes…" data-testid="new-req-notes" /></div>

            <Button type="submit" disabled={loading} className="bg-brand-navy hover:bg-brand-blue" data-testid="new-req-submit">
              {loading ? "Submitting…" : "Submit request"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
